import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

export const StyledToolHero = styled.section`
  background: url(/assets/hero-bg.webp);
  background-size: 100% 100%;
  background-repeat: no-repeat;
  background-position: center 0%;
  color: ${CSSVars.white};
  padding: 140px 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  h1 {
    font-size: 1.875rem;
    font-weight: 300;
    margin-bottom: 20px;
    line-height: 1.15;
    position: relative;
    color: ${CSSVars.white};

    .highlight {
      font-weight: 600;
      background: linear-gradient(90deg, #a5a8ff, #ff979c);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeMD};
    color: rgba(255, 255, 255, 0.8);
    max-width: 580px;
    margin: 0 auto;
    line-height: 1.7;
    font-weight: 300;
    position: relative;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 160px 40px 160px;
    h1 { font-size: ${CSSVars.fontSize2XL}; }
    p  { font-size: ${CSSVars.fontSizeLG}; }
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 180px 40px 180px;
    h1 { font-size: ${CSSVars.fontSize3XL}; }
  }
`;

export const StyledToolBody = styled.div`
  background: ${CSSVars.gray50};
  padding: 60px 20px 80px;

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 80px 40px 100px;
  }
`;

export const StyledCalculator = styled.div`
  max-width: 860px;
  margin: 0 auto;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 20px;
  padding: 28px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  h2 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 28px;
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 48px;
    h2 {
      font-size: ${CSSVars.fontSizeXL};
      margin-bottom: 32px;
    }
  }
`;

export const StyledFormGroup = styled.div`
  margin-bottom: 24px;

  label {
    display: block;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 600;
    color: ${CSSVars.gray700};
    margin-bottom: 8px;
  }

  textarea {
    width: 100%;
    padding: 13px 16px;
    font-size: ${CSSVars.fontSizeSM};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 10px;
    color: ${CSSVars.black};
    background: ${CSSVars.white};
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: ${CSSVars.secondaryFont};
    min-height: 220px;
    resize: vertical;

    &::placeholder {
      color: ${CSSVars.gray400};
      font-size: ${CSSVars.fontSizeXS};
    }

    &:focus {
      border-color: ${CSSVars.blue500};
      box-shadow: 0 0 0 3px rgba(76, 79, 255, 0.1);
      outline: none;
    }
  }

  .hint {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    margin-top: 6px;
    line-height: 1.5;
  }
`;

export const StyledCalculateButton = styled.button`
  width: 100%;
  padding: 15px 24px;
  background: ${CSSVars.blue500};
  color: ${CSSVars.white};
  font-size: ${CSSVars.fontSizeMD};
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.3s, transform 0.1s;
  font-family: ${CSSVars.primaryFont};
  letter-spacing: 0.01em;

  &:hover {
    background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const StyledResult = styled.div<{ status: 'poor' | 'fair' | 'good' }>`
  max-width: 860px;
  margin: 32px auto 0;
  scroll-margin-top: 80px;
  background: ${CSSVars.white};
  border: 1px solid ${({ status }) =>
    status === 'good'
      ? 'rgba(74, 222, 128, 0.4)'
      : status === 'fair'
      ? 'rgba(252, 211, 77, 0.4)'
      : 'rgba(248, 113, 113, 0.4)'};
  border-radius: 20px;
  padding: 28px 20px;
  box-shadow: 0 4px 32px
    ${({ status }) =>
      status === 'good'
        ? 'rgba(74, 222, 128, 0.12)'
        : status === 'fair'
        ? 'rgba(252, 211, 77, 0.12)'
        : 'rgba(248, 113, 113, 0.12)'};

  .result-summary {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
  }

  .summary-left {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .result-label {
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: ${CSSVars.gray500};
  }

  .result-badge {
    display: inline-block;
    padding: 5px 16px;
    border-radius: 20px;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;

    &.good {
      background: rgba(22, 163, 74, 0.1);
      color: #16a34a;
    }

    &.fair {
      background: rgba(180, 83, 9, 0.1);
      color: #b45309;
    }

    &.poor {
      background: rgba(220, 38, 38, 0.1);
      color: #dc2626;
    }
  }

  .summary-stats {
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
  }

  .stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .stat-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: ${CSSVars.black};
    line-height: 1;

    &.has-issues { color: #dc2626; }
    &.no-issues { color: #16a34a; }
  }

  .stat-label {
    font-size: ${CSSVars.fontSizeXS};
    color: ${CSSVars.gray500};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .result-good {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 22px;
    background: rgba(22, 163, 74, 0.06);
    border: 1px solid rgba(22, 163, 74, 0.2);
    border-radius: 10px;
    margin-bottom: 20px;

    svg {
      width: 24px;
      height: 24px;
      color: #16a34a;
      flex-shrink: 0;
    }

    p {
      font-size: ${CSSVars.fontSizeMD};
      color: ${CSSVars.gray700};
      line-height: 1.5;
      strong { color: #16a34a; }
    }
  }

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 40px 48px;
  }
`;

export const StyledIssuesList = styled.div`
  margin-bottom: 24px;

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 12px;
  }

  .issue-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    background: rgba(220, 38, 38, 0.05);
    border: 1px solid rgba(220, 38, 38, 0.15);
    border-radius: 8px;
    margin-bottom: 8px;

    &:last-child { margin-bottom: 0; }

    .issue-icon {
      width: 18px;
      height: 18px;
      color: #dc2626;
      flex-shrink: 0;
      margin-top: 2px;
    }

    p {
      font-size: ${CSSVars.fontSizeSM};
      color: ${CSSVars.gray700};
      line-height: 1.5;
    }
  }
`;

export const StyledHeadingTree = styled.div`
  margin-top: 24px;

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 14px;
  }
`;

export const StyledHeadingNode = styled.div<{ level: number }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  margin-bottom: 4px;
  margin-left: ${({ level }) => (level - 1) * 20}px;
  background: ${({ level }) =>
    level === 1
      ? 'rgba(76, 79, 255, 0.06)'
      : level === 2
      ? 'rgba(76, 79, 255, 0.03)'
      : 'transparent'};
  border-left: 3px solid ${({ level }) =>
    level === 1
      ? CSSVars.blue500
      : level === 2
      ? 'rgba(76, 79, 255, 0.4)'
      : 'rgba(76, 79, 255, 0.15)'};
  border-radius: 0 6px 6px 0;

  .heading-level {
    flex-shrink: 0;
    font-size: ${CSSVars.fontSizeXS};
    font-weight: 700;
    color: ${({ level }) =>
      level === 1 ? CSSVars.blue500 : CSSVars.gray500};
    width: 24px;
    text-align: center;
    padding-top: 1px;
  }

  .heading-text {
    font-size: ${({ level }) =>
      level === 1 ? CSSVars.fontSizeMD : CSSVars.fontSizeSM};
    font-weight: ${({ level }) => (level <= 2 ? 700 : 400)};
    color: ${({ level }) => (level === 1 ? CSSVars.black : CSSVars.gray700)};
    line-height: 1.4;
  }
`;

export const StyledWidgetsSection = styled.section`
  max-width: 1000px;
  margin: 72px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    text-align: center;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    text-align: center;
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 48px;
    font-weight: 300;
  }
`;

export const StyledWidgetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StyledWidgetCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  background: ${CSSVars.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-3px);
  }

  .widget-header {
    display: flex;
    align-items: center;
    gap: 12px;

    img {
      width: 44px;
      height: 44px;
      object-fit: contain;
      border-radius: 10px;
      background: ${CSSVars.gray50};
    }

    h3 {
      font-size: ${CSSVars.fontSizeMD};
      font-weight: 700;
      color: ${CSSVars.black};
      line-height: 1.3;
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    flex: 1;
    font-weight: 300;
  }

  .widget-cta {
    display: inline-block;
    padding: 10px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 8px;
    text-align: center;
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;

export const StyledCtaBanner = styled.section`
  background: ${CSSVars.white};
  padding: 60px 20px 80px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 80px 30px 100px;
  }

  .banner-inner {
    max-width: 1000px;
    margin: 0 auto;
    background: linear-gradient(135deg, #4c4fff 0%, #6b8cff 100%);
    border-radius: 20px;
    padding: 48px 32px;
    text-align: center;
    color: ${CSSVars.white};

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      padding: 64px 48px;
    }
  }

  h2 {
    font-size: ${CSSVars.fontSizeLG};
    font-weight: 700;
    margin-bottom: 16px;
    line-height: 1.3;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: rgba(255, 255, 255, 0.85);
    max-width: 520px;
    margin: 0 auto 28px;
    line-height: 1.7;
    font-weight: 300;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSizeMD};
    }
  }

  .banner-cta {
    display: inline-block;
    padding: 14px 32px;
    background: ${CSSVars.white};
    color: #3538cd;
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 10px;
    text-decoration: none;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  }
`;

export const StyledRelatedTools = styled.section`
  max-width: 1000px;
  margin: 72px auto 0;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${CSSVars.black};
    margin-bottom: 10px;
    text-align: center;
    line-height: 1.2;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: ${CSSVars.fontSize2XL};
    }
  }

  .subtitle {
    text-align: center;
    color: ${CSSVars.gray600};
    font-size: ${CSSVars.fontSizeMD};
    margin-bottom: 48px;
    font-weight: 300;
  }
`;

export const StyledRelatedToolGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StyledRelatedToolCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 24px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  text-decoration: none;
  color: inherit;
  background: ${CSSVars.white};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(76, 79, 255, 0.3);
    box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1);
    transform: translateY(-2px);
  }

  .tool-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(76, 79, 255, 0.08);
    color: ${CSSVars.blue500};

    svg {
      width: 24px;
      height: 24px;
    }
  }

  h3 {
    font-size: ${CSSVars.fontSizeMD};
    font-weight: 700;
    color: ${CSSVars.black};
    line-height: 1.3;
  }

  p {
    font-size: ${CSSVars.fontSizeSM};
    color: ${CSSVars.gray600};
    line-height: 1.65;
    flex: 1;
    font-weight: 300;
  }

  .tool-cta {
    padding: 9px 18px;
    background: ${CSSVars.blue500};
    color: ${CSSVars.white};
    font-size: ${CSSVars.fontSizeSM};
    font-weight: 700;
    border-radius: 8px;
    transition: background 0.3s;

    &:hover {
      background: linear-gradient(270deg, ${CSSVars.pink300} 0%, ${CSSVars.blue500} 100%);
    }
  }
`;
